'use client'

import { C, Callout, Exercise, H, Lesson, M, MB, OL, P, Step, UL } from '../_components/lesson'
import * as EX from './exercises/linear-extra'

export default function ProjectRecommender() {
  return (
    <Lesson>
      <Step>
        <H>Project: a movie recommender</H>
        <P>
          Every streaming service has to guess what you’ll like next. In this project you’ll build one from vectors, dot
          products and PCA. The data: 20 people, 10 movies, and the ratings (1–5 stars) they gave. About a third of the table is
          blank: movies people haven’t seen yet.
        </P>
        <OL>
          <li>Measure how similar two people’s tastes are.</li>
          <li>Predict missing ratings from similar people, and prove it beats a simple baseline.</li>
          <li>Draw a map of the movies, using nothing but who liked what.</li>
        </OL>
      </Step>

      <Step gate="rec-similarity">
        <H>Part 1 · Taste twins</H>
        <P>
          Treat each person’s ratings as a vector, one number per movie. Similar tastes means the vectors point the same way, so
          you can use cosine similarity, with two twists. Compare only movies both people have rated. And first{' '}
          <strong>centre</strong> each person’s ratings on their own average, so a generous rater and a harsh one with the same
          taste still count as twins.
        </P>
        <MB>{'\\text{sim}(u, v) = \\frac{\\mathbf{a} \\cdot \\mathbf{b}}{|\\mathbf{a}|\\,|\\mathbf{b}|} \\qquad \\text{over movies both rated, after centring}'}</MB>
        <Exercise
          id="rec-similarity"
          title="center and similarity"
          {...EX.recSimilarity}
          hints={[
            <>
              center: <C>{'const m = userMean(row)'}</C>, then <C>{'row.map(x => (x > 0 ? x - m : 0))'}</C>.
            </>,
            <>
              In similarity, loop over the movies. Only when <C>{'R[u][m] > 0 && R[v][m] > 0'}</C>, add to the dot product and to
              both squared lengths, and count it as shared.
            </>,
          ]}
        />
      </Step>

      <Step gate="rec-predict">
        <H>Part 2 · Predict, then prove it</H>
        <P>
          To guess what you’d give a movie, ask the people who’ve seen it, and trust each one in proportion to how similar their
          taste is to yours:
        </P>
        <MB>{'\\hat{r}_{u,m} = \\frac{\\sum_v \\text{sim}(u, v)\\; r_{v,m}}{\\sum_v \\text{sim}(u, v)} \\qquad \\text{over people } v \\text{ with sim} > 0 \\text{ who rated } m'}</MB>
        <P>
          Is it any good? The honest way to find out: hide a rating you know, predict it, and compare. Do that for every known
          rating, and you get the average error in stars. Your recommender has to beat the obvious baseline, which guesses each
          movie’s average rating for everyone.
        </P>
        <Exercise
          id="rec-predict"
          title="predict and recommend"
          {...EX.recPredict}
          hints={[
            <>
              Loop over the users <C>v</C>. Skip <C>v === u</C>, people who haven’t rated <C>m</C>, and anyone with{' '}
              <C>{'s <= 0'}</C>. Accumulate <C>{'num += s * R[v][m]'}</C> and <C>{'den += s'}</C>.
            </>,
            <>
              recommend:{' '}
              <C>{'range(R[u].length).filter(m => R[u][m] === 0).sort((a, b) => predict(R, u, b) - predict(R, u, a)).slice(0, k)'}</C>
              , then map the indices to <C>movies[m]</C>.
            </>,
          ]}
        />
        <Callout kind="plain">
          “Hide one, predict it, repeat” is called <strong>leave-one-out</strong> evaluation. It answers the only question that
          matters: how well does this work on ratings it hasn’t seen? Module 6 is built around that question.
        </Callout>
      </Step>

      <Step gate="rec-map">
        <H>Part 3 · A map of taste</H>
        <P>
          Each movie is also a vector: a column of the table, with one rating per person. Run PCA on those columns and every
          movie gets a position, using only who liked what. Nobody tells the algorithm which movies are romances or which are
          action films. Will it work it out?
        </P>
        <Exercise
          id="rec-map"
          title="fillBlanks and movieCoords"
          {...EX.recMap}
          hints={[
            <>
              Work out each movie’s average over its non-zero ratings, then{' '}
              <C>{'R.map(row => row.map((x, m) => (x > 0 ? x : means[m])))'}</C>.
            </>,
            <>
              <C>{'const comps = pca(fillBlanks(R), 2)'}</C>, then{' '}
              <C>{'movies.map((_, i) => [comps[0][i], comps[1][i]])'}</C>.
            </>,
          ]}
        />
        <Callout kind="why">
          This is the seed of an idea that runs through all of modern AI: <strong>learned coordinates</strong>, where nearby
          means similar. Here we found them with PCA. Netflix-style systems learn them with gradient descent, and language
          models learn one for every word. Those are called <em>embeddings</em>, and they’re coming in Module 8.
        </Callout>
      </Step>

      <Step>
        <H>Stretch goals</H>
        <UL>
          <li>
            Only trust the <M>{'k'}</M> most similar people (try 3, 5, 10). Does the leave-one-out error go down?
          </li>
          <li>
            Flip the idea around: <em>item-based</em> recommendation. Compute similarities between movie columns instead, and
            predict your rating of a movie from your ratings of similar movies.
          </li>
          <li>
            Add yourself: rate the movies you’d watch (make up your tastes), append the row to <C>R</C>, and ask for
            recommendations.
          </li>
        </UL>
      </Step>
    </Lesson>
  )
}
